// src/utils/tilePeriod.ts
//
// The period ONE tile refreshes with (plan 2026-10-10 a click on a time value;
// the `tilePeriod` of controls A4b decision 3). Pure, so the rules are tested
// without mounting Insights.
//
// Today one source can move a tile off the dashboard period: a PERIOD CLICK,
// a click on a time value (a month row, a week on a line). It works like a
// click filter, on the period instead of a dimension:
//
// - the clicked tile (the source) keeps the dashboard period and highlights
//   the bucket;
// - every other tile in the source's scope (`interactions.targets`, the same
//   rule as a click filter) gets the bucket CLIPPED to the dashboard window,
//   so a partial first or last bucket is the part the source drew;
// - an empty intersection (the header moved away from the bucket) is no
//   click at all.
//
// The comparison is the counterpart BUCKET, explicit (`comparisonMode:
// "custom"` with its dates), never derived from the clipped range: a custom
// preset compares by LENGTH (`comparisonWindow.ts`), which would set March
// against 29 Jan - 28 Feb. Header "previous period" -> the previous bucket;
// "previous year" -> the same bucket a year earlier (calendar for month,
// quarter, year; the 364-day weekday shift for day and week, as the time
// axis does); "custom" -> the previous bucket; "none" -> none. Clipped by the
// same offsets as the main bucket.
//
// A4b's date range control is the next source, and a block's OWN period
// (plan 2026-10-16-a-dashboard-block-may-carry-its-own-period, ADR-0445) the
// first: `tileWindow` below composes the three.
import { addDays, differenceInCalendarDays, endOfDay, isValid, max as maxDate, min as minDate, parseISO, startOfDay, subYears } from "date-fns";
import { nextBucketStart, truncateToGrain } from "@verity/charts";
import type { ItemPeriod } from "@/api/dashboards";
import { getPresetDates, type TimeRange, type TimeRangePreset } from "@/utils/comparisonWindow";
import {
  computeDefaultTimeGrain,
  formatButtonLabel,
  resolveEffectiveGrain,
  type TimeGrain,
} from "@/components/dashboards/TimeRangeSelector";

/** A period click on the active tab: the bucket (local ISO dates, end
 *  INCLUSIVE), its grain and label, and the tile it came from. */
export type PeriodClick = {
  start: string;
  end: string;
  grain: TimeGrain;
  label: string;
  source: string;
};

type TileRef = { id?: string; block_key?: string };

function inScope(scope: ReadonlySet<string> | undefined, blockKey: string | undefined): boolean {
  if (!scope) return true;
  return blockKey !== undefined && scope.has(blockKey);
}

/** The counterpart bucket's start for the header's comparison mode, or null
 *  when the header compares with nothing. */
function counterpartStart(bucketStart: Date, grain: TimeGrain, mode: TimeRange["comparisonMode"]): Date | null {
  switch (mode) {
    case "none":
      return null;
    case "previous_year":
      return grain === "daily" || grain === "weekly" ? addDays(bucketStart, -364) : subYears(bucketStart, 1);
    case "previous_period":
    case "custom":
    default:
      return truncateToGrain(addDays(bucketStart, -1), grain);
  }
}

/** The bucket of `click` clipped to the dashboard window, as start/end of
 *  day, or null when they do not overlap. */
export function clippedBucket(click: Pick<PeriodClick, "start" | "end">, header: TimeRange): { start: Date; end: Date } | null {
  const start = parseISO(click.start);
  const end = parseISO(click.end);
  if (!isValid(start) || !isValid(end)) return null;
  const clipStart = startOfDay(maxDate([start, header.startDate]));
  const clipEnd = endOfDay(minDate([end, header.endDate]));
  return clipStart.getTime() > clipEnd.getTime() ? null : { start: clipStart, end: clipEnd };
}

/**
 * The period `item` refreshes with under `click`, or null when it follows the
 * dashboard period (no click, the click's own source, a tile outside the
 * source's scope, or a bucket outside the window). `sourceScopes` is
 * `interactionScopesOf(items)` of the active tab.
 */
export function tilePeriod(
  item: TileRef,
  header: TimeRange,
  click: PeriodClick | null | undefined,
  sourceScopes: Readonly<Record<string, ReadonlySet<string>>>,
): TimeRange | null {
  if (!click) return null;
  if (item.id !== undefined && item.id === click.source) return null;
  if (!inScope(sourceScopes[click.source], item.block_key)) return null;
  const bucket = clippedBucket(click, header);
  if (!bucket) return null;

  const range: TimeRange = {
    ...header,
    preset: "custom",
    startDate: bucket.start,
    endDate: bucket.end,
    // The other-period chip is defined only for the header's own modes.
    withOtherPeriod: false,
  };
  const bucketStart = parseISO(click.start);
  const compStart = counterpartStart(bucketStart, click.grain, header.comparisonMode);
  if (!compStart) {
    return { ...range, comparisonMode: "none", comparisonStartDate: undefined, comparisonEndDate: undefined };
  }
  // The counterpart's own end (February is shorter than March), then the
  // same offsets into it as the clip took into the bucket.
  const counterpartEnd = addDays(nextBucketStart(compStart, click.grain), -1);
  const fromStart = differenceInCalendarDays(bucket.start, bucketStart);
  const length = differenceInCalendarDays(bucket.end, bucket.start);
  const cStart = minDate([addDays(compStart, fromStart), counterpartEnd]);
  const cEnd = minDate([addDays(cStart, length), counterpartEnd]);
  return {
    ...range,
    comparisonMode: "custom",
    comparisonStartDate: startOfDay(cStart),
    comparisonEndDate: endOfDay(cEnd),
  };
}

/** The grain a tile on `period` draws at: the header's explicit grain, or
 *  with "auto" the default for that period's length, always clamped to the
 *  grains the dashboard offers (the header's own rule, Insights). */
export function grainForPeriod(
  period: TimeRange,
  selection: "auto" | TimeGrain,
  available: TimeGrain[],
): TimeGrain {
  const preferred = selection === "auto" ? computeDefaultTimeGrain(period.startDate, period.endDate) : selection;
  return resolveEffectiveGrain(preferred, available);
}

/** A stable key of the click for dependency arrays and view keys. */
export function periodClickKey(click: PeriodClick | null | undefined): string {
  return click ? `${click.source}:${click.start}:${click.end}:${click.grain}` : "";
}

/** A stored ISO date as the start or end of that local day, or null. */
function isoDay(value: string | null | undefined, edge: "start" | "end"): Date | null {
  if (!value) return null;
  const day = parseISO(value);
  if (!isValid(day)) return null;
  return edge === "start" ? startOfDay(day) : endOfDay(day);
}

/**
 * A block's own period (`DashboardItemDTO.period`) as the `TimeRange` its tile
 * refreshes over, resolved in the browser like the header and a date range
 * control (one "today" per page), or null when the block follows the
 * dashboard. The comparison is the block's own (D2): the header's mode,
 * target switch and other-period chip never reach it. The target rides only
 * beside a window, as the header's does (ADR-0269; `ItemPeriod` refuses it
 * under `none`). A stored value the browser cannot read (a custom period
 * without dates) is null, so the tile follows the dashboard rather than
 * drawing a window nobody chose.
 */
export function ownPeriodRange(period: ItemPeriod | null | undefined): TimeRange | null {
  if (!period) return null;
  let startDate: Date;
  let endDate: Date;
  if (period.preset === "custom") {
    const start = isoDay(period.start_date, "start");
    const end = isoDay(period.end_date, "end");
    if (!start || !end) return null;
    startDate = start;
    endDate = end;
  } else {
    ({ startDate, endDate } = getPresetDates(period.preset as TimeRangePreset));
  }
  const range: TimeRange = {
    preset: period.preset as TimeRangePreset,
    startDate,
    endDate,
    comparisonMode: period.comparison,
    withOtherPeriod: false,
    ...(period.with_target && period.comparison !== "none" ? { withTarget: true } : {}),
  };
  if (period.comparison === "custom") {
    const cStart = isoDay(period.comparison_start_date, "start");
    const cEnd = isoDay(period.comparison_end_date, "end");
    if (!cStart || !cEnd) return { ...range, comparisonMode: "none", withTarget: undefined };
    return { ...range, comparisonStartDate: cStart, comparisonEndDate: cEnd };
  }
  return range;
}

/**
 * The window ONE tile refreshes over, and how it got there:
 *
 * - `fixed`: the base is the block's OWN period (ADR-0445). The header, its
 *   grain and a date range control do not reach it (D4); its grain is the
 *   automatic one for its own window (Q5).
 * - `own`: the window is a viewer's LOOK — a date range control's period or a
 *   period click's bucket — so the tile refreshes `transient` and outside the
 *   browser cache.
 *
 * The consumers read the two flags per the plan's truth table: bulk takes a
 * tile only when both are false; `transient` and "no browser cache" follow
 * `own`; the cache and the stale check key a `fixed` tile on its own dates.
 * A period click clips onto whichever base the tile has, and a bucket outside
 * a fixed tile's window leaves the tile on that window.
 */
export type TileWindow = { range: TimeRange; grain: TimeGrain; label: string; own: boolean; fixed: boolean };

/** How a tile on `window` refreshes — the truth table's routing rows in one
 *  place: the bulk call takes only the header's window; a look is
 *  `transient` and never touches the browser cache; a fixed window is the
 *  tile's saved state, so it is cached and persisted like the header's. */
export function refreshRouteOf(window: Pick<TileWindow, "own" | "fixed">): {
  bulk: boolean;
  transient: boolean;
  browserCache: boolean;
} {
  return { bulk: !window.own && !window.fixed, transient: window.own, browserCache: !window.own };
}

export type TileWindowContext = {
  header: TimeRange;
  /** The header's effective grain and label, computed once by the page. */
  headerGrain: TimeGrain;
  headerLabel: string;
  grainSelection: "auto" | TimeGrain;
  availableGrains: TimeGrain[];
  /** Per block key, the period of the date range control that reaches it. */
  controlPeriods: ReadonlyMap<string, TimeRange>;
  click: PeriodClick | null | undefined;
  scopes: Readonly<Record<string, ReadonlySet<string>>>;
};

export function tileWindow(item: TileRef & { period?: ItemPeriod | null }, ctx: TileWindowContext): TileWindow {
  const fixedRange = ownPeriodRange(item.period);
  const controlPeriod = !fixedRange && item.block_key ? ctx.controlPeriods.get(item.block_key) ?? null : null;
  const base = fixedRange ?? controlPeriod ?? ctx.header;
  // The header's explicit grain does not reach a fixed tile (Q5).
  const selection = fixedRange ? "auto" : ctx.grainSelection;
  const clicked = tilePeriod(item, base, ctx.click, ctx.scopes);
  if (clicked && ctx.click) {
    return {
      range: clicked,
      grain: grainForPeriod(clicked, selection, ctx.availableGrains),
      label: ctx.click.label,
      own: true,
      fixed: fixedRange !== null,
    };
  }
  if (fixedRange) {
    return {
      range: fixedRange,
      grain: grainForPeriod(fixedRange, "auto", ctx.availableGrains),
      label: formatButtonLabel(fixedRange),
      own: false,
      fixed: true,
    };
  }
  if (controlPeriod) {
    return {
      range: controlPeriod,
      grain: grainForPeriod(controlPeriod, ctx.grainSelection, ctx.availableGrains),
      label: formatButtonLabel(controlPeriod),
      own: true,
      fixed: false,
    };
  }
  return { range: ctx.header, grain: ctx.headerGrain, label: ctx.headerLabel, own: false, fixed: false };
}
